import React, { useCallback, useState } from "react";
import { Badge, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { mapPublicListingStatusToText } from "../../lib/map-public-listing-status-to-text";
import { updatePublicListing } from "../../services/publicListingService";
import { PublicListing } from "../../types/PublicListing";
import ReadOnlyField from "../ReadOnlyField/ReadOnlyField";
import "./PublicListingOverview.scss";

interface PublicListingOverviewProps {
  projectId: string;
  publicListing: PublicListing | null | undefined;
  showManageButton?: boolean;
}

const PublicListingOverview: React.FC<PublicListingOverviewProps> = ({
  projectId,
  publicListing,
  showManageButton = false,
}) => {
  const [isPublishing, setIsPublishing] = useState(false);
  const [isUnpublishing, setIsUnpublishing] = useState(false);

  const handlePublish = useCallback(async () => {
    setIsPublishing(true);
    try {
      await updatePublicListing(projectId, { status: "public" });
      toast.success("Public listing published");
    } catch {
      toast.error("Failed to publish listing");
    } finally {
      setIsPublishing(false);
    }
  }, [projectId]);

  const handleUnpublish = useCallback(async () => {
    setIsUnpublishing(true);
    try {
      await updatePublicListing(projectId, { status: "not-active" });
      toast.success("Public listing unpublished");
    } catch {
      toast.error("Failed to unpublish listing");
    } finally {
      setIsUnpublishing(false);
    }
  }, [projectId]);

  return (
    <section className="public-listing-overview">
      <div className="section-header">Public Listing</div>
      {publicListing ? (
        <div className="stack read-only-fields">
          <ReadOnlyField
            label="Status"
            value={mapPublicListingStatusToText(publicListing.status)}
          />

          {!showManageButton && publicListing.status === "in-preview" && (
            <div className="publish-actions">
              <Button
                variant="primary"
                size="sm"
                onClick={handlePublish}
                disabled={isPublishing}
              >
                {isPublishing ? "Publishing..." : "Publish"}
              </Button>
            </div>
          )}

          {!showManageButton && publicListing.status === "public" && (
            <div className="publish-actions">
              <Button
                variant="outline-primary"
                onClick={handleUnpublish}
                disabled={isUnpublishing}
              >
                {isUnpublishing ? "Unpublishing..." : "Unpublish"}
              </Button>
            </div>
          )}

          {(publicListing.status === "public" ||
            publicListing.status === "in-preview") && (
            <ReadOnlyField
              label="Public Listing URL"
              value={
                <a
                  href={`${window.location.origin}/company/${publicListing.handle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  vcdb.app/company/{publicListing.handle}
                </a>
              }
            />
          )}
          {showManageButton && (
            <Link to={`/project/${projectId}/public-listing`}>
              <Button variant="primary" size="sm">
                Manage listing
              </Button>
            </Link>
          )}
        </div>
      ) : (
        <>
          <div className="listing-status-row">
            <Badge bg="secondary">Not Active</Badge>
          </div>
          <Link to={`/project/${projectId}/public-listing`}>
            Set up a public listing
          </Link>
        </>
      )}
    </section>
  );
};

export default PublicListingOverview;
